import React, { useContext, useState } from 'react';
import { Box, TextField, Button, Typography, CircularProgress, Link, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import AuthService from '../services/AuthService';
import { useTranslation } from 'react-i18next';
import AuthShell from './auth/AuthShell';
import { underlineStyle, primaryButtonSx, errorBoxSx } from './auth/authStyles';

const LoginPage = () => {
    const { t } = useTranslation();
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { login, setHasActiveSubscription } = useContext(AuthContext);
    const navigate = useNavigate();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCredentials(prev => ({ ...prev, [name]: value }));
        if (error) setError(null);
    };

    const handleLogin = async () => {
        setLoading(true);
        setError(null);
        try {
            const userData = await login(credentials);
            let active = false;
            try {
                const account = await AuthService.account();
                active = account.hasActiveSubscription;
                if (account.firstName) localStorage.setItem("FIRST_NAME", account.firstName);
            } catch {
                // account fetch failed; default to showing pricing
            }
            localStorage.setItem("HAS_SUBSCRIPTION", active ? "true" : "false");
            setHasActiveSubscription(active);
            navigate(active ? (userData.role === 'USER' ? '/projects' : '/home') : '/pricing');
        } catch {
            setError(t('login.errorInvalidCredentials'));
        }
        setLoading(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleLogin();
    };

    return (
        <AuthShell formMaxWidth={380}>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', mb: 0.5 }}>
                {t('login.welcomeBack')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#7f8c9a', mb: 4 }}>
                {t('login.signInToContinue')}
            </Typography>

            {/* Error */}
            {error && (
                <Box sx={{ ...errorBoxSx, animation: 'fadeSlideIn 0.2s ease both' }}>
                    <Typography variant="body2" color="error">{error}</Typography>
                </Box>
            )}

            {/* Username */}
            <TextField
                fullWidth
                variant="standard"
                label={t('login.username')}
                name="username"
                value={credentials.username}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                sx={{ mb: 3, ...underlineStyle }}
            />

            {/* Password */}
            <TextField
                fullWidth
                variant="standard"
                type={showPassword ? 'text' : 'password'}
                label={t('login.password')}
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={() => setShowPassword(v => !v)}
                                edge="end"
                                tabIndex={-1}
                            >
                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 1, ...underlineStyle }}
            />

            {/* Forgot password — right-aligned under password field */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
                <Typography variant="caption" sx={{ color: '#7f8c9a' }}>
                    {t('login.forgotPasswordHelp')}
                </Typography>
            </Box>

            {/* Login button */}
            <Button
                fullWidth
                variant="contained"
                disabled={loading}
                onClick={handleLogin}
                sx={primaryButtonSx}
            >
                {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : t('login.login')}
            </Button>

            <Typography variant="body2" sx={{ textAlign: 'center', mt: 3, color: '#7f8c9a' }}>
                {t('login.noAccount')}{' '}
                <Link component={RouterLink} to="/register" sx={{ color: '#427898', fontWeight: 600 }}>
                    {t('login.register')}
                </Link>
            </Typography>
        </AuthShell>
    );
};

export default LoginPage;
